import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Drawer, Image, Input, InputNumber, Radio, Space, Spin, Table, Tag, Typography, message } from 'antd';
import {
  HANDLING_MODE_LABEL,
  InspectionExecution,
  PieceAnswer,
  ReceivingExtraInput,
  ReceivingPlanEntry,
  ReceivingRowInput,
  ReceivingRule,
} from '../../../../types/returnInspection';
import { executeReturnInspection, getReceivingInspectionRules } from '../../../../services/returnInspection';
import { confirmInventoryUnitWeightDimensions, getInventoryUnitByBarcode } from '../../../../services/inventory';
import { speakTextLocal } from '../../../../utils/localSpeech';
import type { LocalSpeechLang } from '../../../../utils/localSpeech';
import type { BoardPlacement } from '../../../../utils/sortingBoardBatch';
import { getAttachmentUrl } from '../../../../utils/attachmentUrl';
import { decidePiece, needsContentCheck, PieceResult, PIECE_RESULT_LABEL } from '../../../../utils/returnDecision';
import { describeFlow } from '../../../return-inspection/decisionFlow';
import { MODE_OUTCOME } from '../../../return-inspection/ruleDisplay';
import type { DecisionFlow } from '../../../../types/returnInspection';

const { Text, Paragraph } = Typography;

interface Props {
  forecast: { id: number; system_code: string };
  /** 要执行的方案：核对一致时是"全部按规则初检"，异常路径是客户确认的方案 */
  plan: ReceivingPlanEntry[];
  /** 核对一致的路径：仓库填的实收清单，随提交一起发给服务端复核；按异常方案执行时不需要 */
  recount?: { rows: ReceivingRowInput[]; extras: ReceivingExtraInput[] };
  unitLabels: Record<number, string>;
  trackingNumber: string;
  onBack: () => void;
  onClose: () => void;
  /** 提交成功后调用：外层据此把入库的货加入分拣看板、触发建层轮询；返回各库存单元分配到的临时分拣仓位，完成页据此显示和播报 */
  onExecuted?: (execution: InspectionExecution) => BoardPlacement[] | void;
  /** 语音播报：和扫描入库页面选的播报语言、是否支持语音保持一致 */
  speechLang?: LocalSpeechLang;
  speechEnabled?: boolean;
}

/** 重量尺寸确认要用到的库存单元信息（来自库存单元详情） */
interface UnitWeightInfo {
  id: number;
  barcode: string;
  /** 第一个分拣条码（预报外货品指定库存单元后，标题里显示它） */
  sortingCode: string;
  unitType: string;
  weight: number;
  length: number;
  width: number;
  height: number;
  confirmed: boolean;
}

interface ActualInput {
  weight: number | null;
  length: number | null;
  width: number | null;
  height: number | null;
}

const toWhole = (value: unknown): number => Math.max(1, Math.round(Number(value) || 0));

const SOURCE_NAME: Record<'unit' | 'project' | 'customer', string> = { unit: '库存单元自设', project: '项目级规则', customer: '通用规则' };

/** 一个带标题条的分区（定义在组件外面，避免每次渲染都重建组件导致里面的输入框失去焦点） */
const Section: React.FC<{ title: string; accent: string; extra?: React.ReactNode; children: React.ReactNode }> = ({ title, accent, extra, children }) => (
  <Card size="small" style={{ marginTop: 16, borderLeft: `4px solid ${accent}` }} title={<Text strong>{title}</Text>} extra={extra}>
    {children}
  </Card>
);

interface PieceState {
  appearance: boolean | null;
  content: boolean | null;
}

const RESULT_COLOR: Record<PieceResult, string> = { resell: 'green', aiu: 'orange', destroy: 'red' };

const errorText = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (typeof data.detail === 'string') return data.detail;
  const first = Object.values(data)[0] as any;
  if (Array.isArray(first)) return typeof first[0] === 'string' ? first[0] : fallback;
  if (typeof first === 'string') return first;
  return fallback;
};

/**
 * 退货入库初检 - 按方案执行：
 * 销毁 / AIU / 正常入库的项目直接确认；"按预设规则初检"的项目，条件判定模式逐件填外观（必要时内件）检查结果，
 * 一律销毁/一律AIU 的规则不需要检查。有 AIU 的件要录入 AIU 总重量。提交后由服务端在一个事务里完成
 * 入库记录、库存动作和执行记录（服务端会重新判定每一件，不信任这里的预览结果）。
 */
const ReturnInspectionWizard: React.FC<Props> = ({ forecast, plan, recount, unitLabels, trackingNumber, onBack, onClose, onExecuted, speechLang, speechEnabled }) => {
  const [rules, setRules] = useState<Record<number, { source: 'unit' | 'project' | 'customer'; rule: ReceivingRule }>>({});
  const [loadingRules, setLoadingRules] = useState(true);
  const [rulesError, setRulesError] = useState('');
  const [pieces, setPieces] = useState<Record<string, PieceState[]>>({});
  const [weight, setWeight] = useState<number | null>(null);
  const [destroyWeight, setDestroyWeight] = useState<number | null>(null);
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<InspectionExecution | null>(null);
  const [placements, setPlacements] = useState<BoardPlacement[]>([]);
  // 右侧抽屉里正在看的货品（检查流程和参考资料）
  const [referenceKey, setReferenceKey] = useState<string | null>(null);

  // 正常入库的库存单元必须先确认重量尺寸（和普通扫描入库一致；服务端也会拦）
  const [unitInfo, setUnitInfo] = useState<Record<number, UnitWeightInfo>>({});
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [unitsError, setUnitsError] = useState('');
  const [confirmedIds, setConfirmedIds] = useState<Record<number, boolean>>({});
  const [actuals, setActuals] = useState<Record<number, ActualInput>>({});
  const [confirmingId, setConfirmingId] = useState<number | null>(null);

  const inspectEntries = useMemo(() => plan.filter(entry => entry.mode === 'inspect'), [plan]);
  const inspectUnitIds = useMemo(
    () => Array.from(new Set(inspectEntries.map(entry => entry.inventory_unit).filter((id): id is number => !!id))).sort((a, b) => a - b),
    [inspectEntries]
  );

  useEffect(() => {
    if (inspectUnitIds.length === 0) {
      setLoadingRules(false);
      return undefined;
    }
    let cancelled = false;
    setLoadingRules(true);
    getReceivingInspectionRules(forecast.id, inspectUnitIds)
      .then(hits => {
        if (cancelled) return;
        const map: Record<number, { source: 'unit' | 'project' | 'customer'; rule: ReceivingRule }> = {};
        hits.forEach(hit => { map[hit.inventory_unit_id] = { source: hit.source, rule: hit.rule }; });
        setRules(map);
        // 条件判定模式的项目：按件数准备逐件答案
        setPieces(prev => {
          const next = { ...prev };
          inspectEntries.forEach(entry => {
            const hit = entry.inventory_unit ? map[entry.inventory_unit] : undefined;
            if (hit?.rule.mode === 'conditional' && !next[entry.key]) {
              next[entry.key] = Array.from({ length: entry.quantity }, () => ({ appearance: null, content: null }));
            }
          });
          return next;
        });
      })
      .catch(err => { if (!cancelled) setRulesError(errorText(err, '加载检查规则失败，请重试')); })
      .finally(() => { if (!cancelled) setLoadingRules(false); });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecast.id, inspectUnitIds.join(',')]);

  // 可能正常入库的库存单元（方案里选了"正常入库"或"按规则初检"的），取它们的重量尺寸和确认状态
  const weightCandidates = useMemo(() => {
    const map = new Map<number, string>();
    plan.forEach(entry => {
      if ((entry.mode === 'normal' || entry.mode === 'inspect') && entry.inventory_unit) {
        const code = entry.inventory_unit_barcode || (entry.source === 'forecast_line' ? entry.description : '');
        if (code) map.set(entry.inventory_unit, code);
      }
    });
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [plan]);
  const candidateKey = weightCandidates.map(([id]) => id).join(',');

  useEffect(() => {
    if (weightCandidates.length === 0) return undefined;
    let cancelled = false;
    setLoadingUnits(true);
    setUnitsError('');
    Promise.all(weightCandidates.map(([, code]) => getInventoryUnitByBarcode(code)))
      .then(responses => {
        if (cancelled) return;
        const info: Record<number, UnitWeightInfo> = {};
        const inputs: Record<number, ActualInput> = {};
        responses.forEach(res => {
          const unit: any = res.data;
          info[unit.id] = {
            id: unit.id,
            barcode: unit.system_barcode,
            sortingCode: ((unit.content?.items || []) as any[])
              .map(item => (item.sorting_codes || [])[0]?.code)
              .find(Boolean) || '',
            unitType: unit.unit_type,
            weight: Number(unit.weight) || 0,
            length: Number(unit.length) || 0,
            width: Number(unit.width) || 0,
            height: Number(unit.height) || 0,
            confirmed: Boolean(unit.weight_dimensions_confirmed),
          };
          // 实际值默认取理论值（取整，至少 1），仓库按实物修改
          inputs[unit.id] = { weight: toWhole(unit.weight), length: toWhole(unit.length), width: toWhole(unit.width), height: toWhole(unit.height) };
        });
        setUnitInfo(info);
        setActuals(prev => ({ ...inputs, ...prev }));
      })
      .catch(err => { if (!cancelled) setUnitsError(errorText(err, '加载库存单元重量尺寸失败，请重试')); })
      .finally(() => { if (!cancelled) setLoadingUnits(false); });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateKey]);

  const ruleOf = (entry: ReceivingPlanEntry) => (entry.inventory_unit ? rules[entry.inventory_unit] : undefined);

  const labelOf = (entry: ReceivingPlanEntry) => {
    // 预报内：库存单元编码 产品名称(第一个分拣条码)，由外层按预报明细生成
    if (entry.source === 'forecast_line') return unitLabels[entry.inventory_unit as number] || entry.description;
    const sortingCode = entry.inventory_unit ? unitInfo[entry.inventory_unit]?.sortingCode : '';
    return `${entry.description}${entry.inventory_unit_barcode ? `（指定为 ${entry.inventory_unit_barcode}${sortingCode ? `，分拣条码 ${sortingCode}` : ''}）` : ''}`;
  };

  const missingRule = inspectEntries.filter(entry => !ruleOf(entry)).length;

  // 逐项的结果件数（预览）
  const tally = useMemo(() => {
    const totals = { resell: 0, aiu: 0, destroy: 0, pending: 0, inspected: 0, content: 0 };
    const resellByUnit: Record<number, number> = {};
    const addResell = (unitId: number | null, qty: number) => {
      if (unitId) resellByUnit[unitId] = (resellByUnit[unitId] || 0) + qty;
    };
    plan.forEach(entry => {
      if (entry.mode === 'destroy') totals.destroy += entry.quantity;
      else if (entry.mode === 'aiu') totals.aiu += entry.quantity;
      else if (entry.mode === 'normal') { totals.resell += entry.quantity; addResell(entry.inventory_unit, entry.quantity); }
      else {
        const hit = entry.inventory_unit ? rules[entry.inventory_unit] : undefined;
        if (!hit) { totals.pending += entry.quantity; return; }
        if (hit.rule.mode === 'always_destroy') totals.destroy += entry.quantity;
        else if (hit.rule.mode === 'always_aiu') totals.aiu += entry.quantity;
        else {
          (pieces[entry.key] || []).forEach(piece => {
            const outcome = decidePiece(hit.rule, piece.appearance, piece.content);
            if (!outcome) { totals.pending += 1; return; }
            totals[outcome.result] += 1;
            if (outcome.result === 'resell') addResell(entry.inventory_unit, 1);
            totals.inspected += 1;
            if (outcome.contentChecked) totals.content += 1;
          });
        }
      }
    });
    return { ...totals, resellByUnit };
  }, [plan, rules, pieces]);

  // 最终会正常入库、但重量尺寸还没确认的库存单元（AIU 不需要确认）
  const pendingConfirmIds = Object.keys(tally.resellByUnit)
    .map(Number)
    .filter(id => {
      const info = unitInfo[id];
      return tally.resellByUnit[id] > 0 && !!info && info.unitType !== 'AIU' && !info.confirmed && !confirmedIds[id];
    });

  const setActual = (id: number, patch: Partial<ActualInput>) => setActuals(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const handleConfirmUnit = async (id: number) => {
    const actual = actuals[id];
    if (!actual || [actual.weight, actual.length, actual.width, actual.height].some(v => !v || v < 1)) {
      message.warning('重量和长宽高都必须填写（整数，至少 1）');
      return;
    }
    setConfirmingId(id);
    try {
      await confirmInventoryUnitWeightDimensions(id, {
        actual_weight: actual.weight as number,
        actual_length: actual.length as number,
        actual_width: actual.width as number,
        actual_height: actual.height as number,
      });
      setConfirmedIds(prev => ({ ...prev, [id]: true }));
      message.success('重量尺寸已确认');
    } catch (err: any) {
      message.error(errorText(err, '确认重量尺寸失败，请重试'));
    } finally {
      setConfirmingId(null);
    }
  };

  // 完成页：逐个播报临时分拣仓位号（和普通入库扫描一样，只报仓位号；首个打断之前没读完的）
  const announcePlacements = () => {
    if (!speechEnabled) return;
    placements.forEach((placement, index) => {
      speakTextLocal(String(placement.temporaryLocation), {
        lang: speechLang || 'zh-CN',
        rate: 1,
        pitch: 1,
        volume: 1,
        interrupt: index === 0,
      });
    });
  };
  useEffect(() => {
    if (done && placements.length > 0) announcePlacements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, placements]);

  const updatePiece = (key: string, index: number, patch: Partial<PieceState>, rule: ReceivingRule) => {
    setPieces(prev => {
      const list = [...(prev[key] || [])];
      const next = { ...list[index], ...patch };
      // 外观结果变了，原来的内件结果可能就不需要了
      if (!needsContentCheck(rule, next.appearance)) next.content = null;
      list[index] = next;
      return { ...prev, [key]: list };
    });
  };

  const bulk = (key: string, rule: ReceivingRule, field: 'appearance' | 'content', value: boolean) => {
    setPieces(prev => ({
      ...prev,
      [key]: (prev[key] || []).map(piece => {
        if (field === 'appearance') {
          const next = { ...piece, appearance: value };
          if (!needsContentCheck(rule, next.appearance)) next.content = null;
          return next;
        }
        return needsContentCheck(rule, piece.appearance) ? { ...piece, content: value } : piece;
      }),
    }));
  };

  const handleSubmit = async () => {
    const answers: Record<string, PieceAnswer[]> = {};
    inspectEntries.forEach(entry => {
      const hit = ruleOf(entry);
      if (hit?.rule.mode !== 'conditional') return;
      answers[entry.key] = (pieces[entry.key] || []).map(piece => ({
        appearance: Boolean(piece.appearance),
        content: needsContentCheck(hit.rule, piece.appearance) ? piece.content : null,
      }));
    });
    setSubmitting(true);
    try {
      const execution = await executeReturnInspection({
        forecast: forecast.id,
        rows: recount?.rows,
        extras: recount?.extras,
        answers,
        aiu_total_weight_g: tally.aiu > 0 ? weight : null,
        destroy_total_weight_g: tally.destroy > 0 ? destroyWeight : null,
        remarks: remarks.trim(),
        tracking_number: trackingNumber,
      });
      setDone(execution);
      message.success('退货入库初检已完成，入库记录已创建');
      try {
        const result = onExecuted?.(execution);
        if (Array.isArray(result)) setPlacements(result);
      } catch (err) {
        console.error('[退货初检] 入库后处理失败（分拣看板/建层）', err);
      }
    } catch (err: any) {
      message.error(errorText(err, '提交失败，请重试'));
    } finally {
      setSubmitting(false);
    }
  };

  const referenceMaterial = (rule: ReceivingRule) => {
    const flow = rule.decision_flow as DecisionFlow;
    const photos = (list: ReceivingRule['packaging_baseline_photos'], title: string) =>
      list.length === 0 ? null : (
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>{title}</Text>
          <div>
            <Image.PreviewGroup>
              <Space size={6} wrap>
                {list.map(p => (
                  <Image key={p.url} src={getAttachmentUrl({ url: p.url })} width={110} height={110} style={{ objectFit: 'cover', borderRadius: 4 }} />
                ))}
              </Space>
            </Image.PreviewGroup>
          </div>
        </div>
      );
    const checklist = rule.content_checklist.filter(row => row.item.trim());
    return (
      <div>
        <div style={{ marginBottom: 8 }}>
          {describeFlow(flow, rule.packaging_sealed).map((line, i) => (
            <div key={i} style={{ fontSize: 12 }}>{line}</div>
          ))}
        </div>
        {photos(rule.packaging_baseline_photos, '外观参考照片')}
        {rule.packaging_notes ? <Paragraph style={{ fontSize: 12 }}>外观说明：{rule.packaging_notes}</Paragraph> : null}
        {photos(rule.content_baseline_photos, '内件参考照片')}
        {checklist.length > 0 && (
          <div style={{ fontSize: 12, marginBottom: 6 }}>
            内件物品清单：{checklist.map(row => `${row.item} ×${row.qty}`).join('、')}
          </div>
        )}
        {rule.content_notes ? <Paragraph style={{ fontSize: 12 }}>内件说明：{rule.content_notes}</Paragraph> : null}
      </div>
    );
  };

  // ---------------- 完成 ----------------
  if (done) {
    return (
      <Card size="small" title="退货入库初检 · 已完成">
        <Alert
          type="success"
          showIcon
          message="退货入库初检已完成"
          description={
            <div>
              <div>入库记录：<Text code>{done.inbound_record_code || done.inbound_record}</Text>　实收 {done.received_total} 件</div>
              <div>
                <Tag color="green">正常品入库 {done.normal_qty} 件</Tag>
                <Tag color="orange">异常品入库 {done.aiu_qty} 件</Tag>
                <Tag color="red">销毁 {done.destroy_qty} 件{done.destroy_total_weight_g ? `（总重量 ${done.destroy_total_weight_g} 克）` : ''}</Tag>
              </div>
              <div style={{ fontSize: 12, marginTop: 4 }}>
                共执行初检判断 {done.inspected_count} 件（外观检查 {done.appearance_checked_count} 件，其中进入内件检查 {done.content_checked_count} 件）
              </div>
            </div>
          }
        />
        {(done.inbound_items || []).length > 0 && (
          <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
            正常品入库和异常品（AIU）入库的件已加入页面下方的"入库分拣看板"（已分配临时分拣仓位），请在看板里生成仓储容器并上架，和普通入库一样。
          </Paragraph>
        )}
        {placements.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <Space style={{ marginBottom: 6 }}>
              <Text strong>请把货放入对应的临时分拣仓位</Text>
              {speechEnabled && <Button size="small" onClick={announcePlacements}>重新播报</Button>}
            </Space>
            <Table
              size="small"
              pagination={false}
              rowKey={(row: BoardPlacement & { index: number }) => `${row.inventoryUnitCode}-${row.index}`}
              dataSource={placements.map((placement, index) => ({ ...placement, index }))}
              columns={[
                {
                  title: '库存单元',
                  key: 'unit',
                  render: (_: unknown, row: BoardPlacement) => {
                    const item = (done.inbound_items || []).find(i => i.inventory_unit_barcode === row.inventoryUnitCode);
                    return (
                      <span>
                        <Text code>{row.inventoryUnitCode}</Text>
                        {item?.unit_type === 'AIU' ? <Tag color="orange" style={{ marginLeft: 6 }}>AIU</Tag> : null}
                        {item?.unit_name ? <Text type="secondary"> {item.unit_name}</Text> : null}
                      </span>
                    );
                  },
                },
                { title: '数量', dataIndex: 'quantity', key: 'quantity', width: 90, align: 'center' as const },
                {
                  title: '临时分拣仓位',
                  key: 'location',
                  width: 160,
                  align: 'center' as const,
                  render: (_: unknown, row: BoardPlacement) => (
                    <Text strong style={{ color: '#cf1322', fontSize: 22, background: '#fff566', border: '1px solid #ffec3d', borderRadius: 4, padding: '2px 14px' }}>
                      {row.temporaryLocation}
                    </Text>
                  ),
                },
              ]}
            />
          </div>
        )}
        <Button type="primary" style={{ marginTop: 12 }} onClick={onClose}>完成并关闭</Button>
      </Card>
    );
  }

  // ---------------- 分区：预报内 / 预报外 / 重量尺寸确认 / 汇总 / 入库信息与提交 ----------------
  const forecastEntries = plan.filter(entry => entry.source === 'forecast_line');
  const extraEntries = plan.filter(entry => entry.source === 'extra');
  const totalQuantity = plan.reduce((sum, entry) => sum + entry.quantity, 0);
  const quantityOf = (entries: ReceivingPlanEntry[]) => entries.reduce((sum, entry) => sum + entry.quantity, 0);

  const itemBoxStyle: React.CSSProperties = { border: '1px solid #f0f0f0', borderRadius: 6, padding: 12, marginBottom: 12, background: '#fff' };

  const renderEntry = (entry: ReceivingPlanEntry) => {
    const hit = ruleOf(entry);
    const list = pieces[entry.key] || [];
    const conditionalRule = entry.mode === 'inspect' && hit?.rule.mode === 'conditional' ? hit.rule : null;
    const decided = conditionalRule ? list.filter(piece => decidePiece(conditionalRule, piece.appearance, piece.content)).length : 0;
    const header = (
      <Space wrap>
        <Text strong>{labelOf(entry)}</Text>
        <Text type="secondary">{entry.quantity} 件</Text>
        <Tag>{HANDLING_MODE_LABEL[entry.mode]}</Tag>
        {conditionalRule && (
          <Tag color={decided === list.length ? 'green' : 'blue'}>
            {decided === list.length ? '已填完' : '已填'} {decided}/{list.length} 件
          </Tag>
        )}
      </Space>
    );

    let body: React.ReactNode = null;
    if (entry.mode === 'destroy') body = <Text type="secondary" style={{ fontSize: 12 }}>直接销毁，不入库，销毁费按销毁总重量收取。</Text>;
    else if (entry.mode === 'aiu') body = <Text type="secondary" style={{ fontSize: 12 }}>不检查，直接作为异常品（AIU）入库。</Text>;
    else if (entry.mode === 'normal') body = <Text type="secondary" style={{ fontSize: 12 }}>不检查，直接作为正常品入库。</Text>;
    else if (!hit) body = null;
    else if (hit.rule.mode !== 'conditional') {
      body = (
        <Text type="secondary" style={{ fontSize: 12 }}>
          当前适用规则：{SOURCE_NAME[hit.source]}，{MODE_OUTCOME[hit.rule.mode]}，无需检查
        </Text>
      );
    } else {
      const rule = hit.rule;
      const contentNeeded = list.some(piece => needsContentCheck(rule, piece.appearance));
      body = (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>当前适用规则：{SOURCE_NAME[hit.source]}，逐件检查（外观 → 内件）</Text>
            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setReferenceKey(entry.key)}>
              查看检查流程和参考资料
            </Button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-start', margin: '4px 0 6px' }}>
            <Space wrap>
              <Button size="small" onClick={() => bulk(entry.key, rule, 'appearance', true)}>全部外观一致</Button>
              <Button size="small" onClick={() => bulk(entry.key, rule, 'appearance', false)}>全部外观不一致</Button>
              {contentNeeded && <Button size="small" onClick={() => bulk(entry.key, rule, 'content', true)}>需检查内件的全部内件一致</Button>}
              {contentNeeded && <Button size="small" onClick={() => bulk(entry.key, rule, 'content', false)}>需检查内件的全部内件不一致</Button>}
            </Space>
          </div>
          <Table
            size="small"
            pagination={false}
            rowKey="index"
            dataSource={list.map((piece, index) => ({ ...piece, index }))}
            columns={[
              { title: '第几件', dataIndex: 'index', key: 'index', width: 70, render: (i: number) => i + 1 },
              {
                title: rule.packaging_sealed ? '外观（包装完好=一致）' : '外观是否与参考一致',
                key: 'appearance',
                width: 220,
                render: (_: unknown, row: PieceState & { index: number }) => (
                  <Radio.Group
                    optionType="button"
                    buttonStyle="solid"
                    value={row.appearance === null ? undefined : row.appearance}
                    onChange={e => updatePiece(entry.key, row.index, { appearance: e.target.value }, rule)}
                    options={[{ label: '一致', value: true }, { label: '不一致', value: false }]}
                  />
                ),
              },
              {
                title: '内件是否与参考一致',
                key: 'content',
                width: 220,
                render: (_: unknown, row: PieceState & { index: number }) =>
                  needsContentCheck(rule, row.appearance) ? (
                    <Radio.Group
                      optionType="button"
                      buttonStyle="solid"
                      value={row.content === null ? undefined : row.content}
                      onChange={e => updatePiece(entry.key, row.index, { content: e.target.value }, rule)}
                      options={[{ label: '一致', value: true }, { label: '不一致', value: false }]}
                    />
                  ) : (
                    <Text type="secondary">{row.appearance === null ? '-' : '无需检查'}</Text>
                  ),
              },
              {
                title: '结果',
                key: 'result',
                render: (_: unknown, row: PieceState & { index: number }) => {
                  const outcome = decidePiece(rule, row.appearance, row.content);
                  return outcome ? <Tag color={RESULT_COLOR[outcome.result]}>{PIECE_RESULT_LABEL[outcome.result]}</Tag> : <Text type="secondary">待填写</Text>;
                },
              },
            ]}
          />
        </div>
      );
    }
    return (
      <div key={entry.key} style={itemBoxStyle}>
        <div style={{ marginBottom: body ? 6 : 0 }}>{header}</div>
        {body}
      </div>
    );
  };

  const referenceEntry = referenceKey ? plan.find(entry => entry.key === referenceKey) : undefined;
  const referenceHit = referenceEntry ? ruleOf(referenceEntry) : undefined;

  // 提交按钮不能点的原因，逐条列出来，免得对着禁用的按钮不知道还差什么
  const blockers: string[] = [];
  if (loadingRules) blockers.push('正在加载检查规则…');
  if (rulesError) blockers.push('检查规则加载失败');
  if (!loadingRules && missingRule > 0) blockers.push(`有 ${missingRule} 项没有生效的初检规则`);
  if (tally.pending > 0) blockers.push(`还有 ${tally.pending} 件检查结果没填完`);
  if (loadingUnits) blockers.push('正在加载库存单元信息…');
  if (unitsError) blockers.push('库存单元信息加载失败');
  if (pendingConfirmIds.length > 0) blockers.push(`${pendingConfirmIds.length} 个库存单元还没确认重量尺寸`);
  if (tally.aiu > 0 && !(weight !== null && weight >= 1)) blockers.push('请填写 AIU 总重量');
  if (tally.destroy > 0 && !(destroyWeight !== null && destroyWeight >= 1)) blockers.push('请填写销毁总重量');
  const canSubmit = blockers.length === 0;

  const summaryTile = (label: string, value: number, color: string) => (
    <div style={{ flex: 1, minWidth: 140, border: `1px solid ${color}`, borderRadius: 6, padding: '8px 12px', textAlign: 'center' }}>
      <div style={{ fontSize: 24, fontWeight: 700, color, lineHeight: '30px' }}>{value}</div>
      <div style={{ fontSize: 12, color: '#595959' }}>{label}</div>
    </div>
  );

  return (
    <Card size="small" title="退货入库初检 · 按方案执行">
      <div style={{ fontSize: 12, color: '#8c8c8c' }}>
        预报 <Text code>{forecast.system_code}</Text>　物流单号 <Text code>{trackingNumber || '-'}</Text>　实收合计 {totalQuantity} 件
      </div>
      {loadingRules && <Spin tip="加载检查规则…"><div style={{ height: 40 }} /></Spin>}
      {rulesError && <Alert type="error" showIcon message={rulesError} style={{ marginTop: 12 }} />}
      {!loadingRules && missingRule > 0 && (
        <Alert type="error" showIcon style={{ marginTop: 12 }} message={`有 ${missingRule} 项当前没有生效的初检规则，无法按规则初检。请联系客户确认，或撤销异常后重新处理。`} />
      )}

      {forecastEntries.length > 0 && (
        <Section title="预报内货品" accent="#1677ff" extra={<Text type="secondary">共 {forecastEntries.length} 项 / {quantityOf(forecastEntries)} 件</Text>}>
          {forecastEntries.map(renderEntry)}
        </Section>
      )}

      {extraEntries.length > 0 && (
        <Section title="预报外货品" accent="#cf1322" extra={<Text type="secondary">共 {extraEntries.length} 项 / {quantityOf(extraEntries)} 件</Text>}>
          {extraEntries.map(renderEntry)}
        </Section>
      )}

      {unitsError && <Alert type="error" showIcon style={{ marginTop: 16 }} message={unitsError} />}
      {pendingConfirmIds.length > 0 && (
        <Section title="产品重量尺寸确认" accent="#faad14" extra={<Text type="warning">提交前必须先确认</Text>}>
          <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
            将正常品入库的库存单元需要先确认重量尺寸，和普通入库一样。
          </Paragraph>
          <Table
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={pendingConfirmIds.map(id => unitInfo[id])}
            columns={[
              {
                title: '库存单元',
                key: 'unit',
                width: 220,
                render: (_: unknown, info: UnitWeightInfo) => (
                  <div>
                    <Text code>{info.barcode}</Text>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>将正常品入库 {tally.resellByUnit[info.id]} 件</div>
                  </div>
                ),
              },
              {
                title: '理论值',
                key: 'theory',
                width: 170,
                render: (_: unknown, info: UnitWeightInfo) => (
                  <span style={{ fontSize: 12 }}>{info.weight} 克；{info.length}×{info.width}×{info.height} 厘米</span>
                ),
              },
              {
                title: '实际重量（克）/ 长×宽×高（厘米）',
                key: 'actual',
                render: (_: unknown, info: UnitWeightInfo) => {
                  const actual = actuals[info.id] || { weight: null, length: null, width: null, height: null };
                  const field = (key: keyof ActualInput, unit: string, label: string) => (
                    <InputNumber
                      min={1}
                      precision={0}
                      style={{ width: 96 }}
                      placeholder={label}
                      addonAfter={unit}
                      value={actual[key]}
                      onChange={value => setActual(info.id, { [key]: typeof value === 'number' ? value : null })}
                    />
                  );
                  return (
                    <Space size={4} wrap>
                      {field('weight', '克', '重量')}
                      {field('length', '厘米', '长')}
                      {field('width', '厘米', '宽')}
                      {field('height', '厘米', '高')}
                    </Space>
                  );
                },
              },
              {
                title: '',
                key: 'confirm',
                width: 110,
                render: (_: unknown, info: UnitWeightInfo) => (
                  <Button type="primary" size="small" loading={confirmingId === info.id} onClick={() => handleConfirmUnit(info.id)}>
                    确认重量尺寸
                  </Button>
                ),
              },
            ]}
          />
        </Section>
      )}

      <Section title="汇总" accent="#52c41a">
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {summaryTile('正常品入库', tally.resell, '#52c41a')}
          {summaryTile('异常品（AIU）入库', tally.aiu, '#fa8c16')}
          {summaryTile('销毁', tally.destroy, '#cf1322')}
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
          共执行初检判断 {tally.inspected} 件（外观检查 {tally.inspected} 件，其中进入内件检查 {tally.content} 件）。销毁的件不会进入入库记录。
          {tally.pending > 0 ? <Text type="danger">　还有 {tally.pending} 件没有填完检查结果</Text> : null}
        </div>
      </Section>

      <Section title="入库信息" accent="#722ed1">
        {tally.aiu > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Text strong>AIU 总重量：</Text>
            <InputNumber
              min={1}
              max={100000000}
              precision={0}
              style={{ width: 160, marginLeft: 8 }}
              placeholder="本次所有 AIU 件合计"
              addonAfter="克"
              value={weight}
              onChange={value => setWeight(typeof value === 'number' ? value : null)}
            />
            <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>必填，用于异常品的上架费梯度</Text>
          </div>
        )}
        {tally.destroy > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Text strong>销毁总重量：</Text>
            <InputNumber
              min={1}
              max={100000000}
              precision={0}
              style={{ width: 160, marginLeft: 8 }}
              placeholder="本次所有销毁件合计"
              addonAfter="克"
              value={destroyWeight}
              onChange={value => setDestroyWeight(typeof value === 'number' ? value : null)}
            />
            <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>必填，销毁费按这个总重量收取</Text>
          </div>
        )}
        <Input.TextArea
          rows={2}
          maxLength={500}
          placeholder="备注（选填）"
          value={remarks}
          onChange={e => setRemarks(e.target.value)}
        />
        <Space style={{ marginTop: 12 }} wrap align="center">
          <Button type="primary" loading={submitting} disabled={!canSubmit} onClick={handleSubmit}>
            提交初检结果并入库
          </Button>
          <Button onClick={onBack} disabled={submitting}>返回上一步</Button>
          {!canSubmit && <Text type="secondary" style={{ fontSize: 12 }}>暂时不能提交：{blockers.join('；')}</Text>}
        </Space>
      </Section>
      {/* 检查流程和参考资料：右侧抽屉、不要遮罩——仓库对着参考照片逐件填写时，后面的表格照常可以操作 */}
      <Drawer
        title={referenceEntry ? (
          <Space wrap>
            <Text strong>{labelOf(referenceEntry)}</Text>
            {referenceHit ? <Tag>{SOURCE_NAME[referenceHit.source]}</Tag> : null}
          </Space>
        ) : '检查流程和参考资料'}
        open={!!referenceEntry && !!referenceHit}
        mask={false}
        width={380}
        onClose={() => setReferenceKey(null)}
        destroyOnClose
      >
        {referenceHit ? referenceMaterial(referenceHit.rule) : null}
      </Drawer>
    </Card>
  );
};

export default ReturnInspectionWizard;
